// Quick-add input that accepts task-line syntax and shows a parsed preview.
import { useMemo, useState } from "react";
import type { Actor, Bucket, CreateItem, Status } from "../api/types";
import { quickAddToCreate } from "../lib/quickadd";
import { humanDate } from "../lib/format";
import { resolveDate } from "../editor/tasktokens";

export function QuickAdd({
  onCreate,
  statuses,
  actors,
  defaultStatusId,
  buckets,
  placeholder = "Add a task…  (A) title @person #label due:fri",
  label = "Add task",
  compact,
}: {
  onCreate: (body: CreateItem) => Promise<unknown>;
  statuses?: Status[];
  actors?: Actor[];
  defaultStatusId?: string;
  /** v0.4: resolve `bucket:Name` tokens (collections with buckets). */
  buckets?: Bucket[];
  placeholder?: string;
  label?: string;
  compact?: boolean;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const result = useMemo(
    () => (text.trim() ? quickAddToCreate(text, { statuses, actors, defaultStatusId, buckets }) : null),
    [text, statuses, actors, defaultStatusId, buckets],
  );

  const submit = async () => {
    if (!result || !result.create.title.trim() || busy) return;
    setBusy(true);
    try {
      await onCreate(result.create);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  const p = result?.parsed;
  const hasTokens =
    p && (p.priority !== null || p.due || p.start || p.labels.length || p.people.length || p.status || p.projects.length || p.bucket);

  return (
    <form
      className={`quick-add${compact ? " compact" : ""}`}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        disabled={busy}
        onKeyDown={(e) => {
          if (e.key === "Escape") setText("");
        }}
      />
      {hasTokens && p && (
        <div className="quick-add-preview" aria-live="polite">
          <span className="qa-title">{p.title || "(no title)"}</span>
          {p.priorityLetter && <span className="chip">({p.priorityLetter})</span>}
          {p.due && (
            <span className="chip chip-date">due {resolveDate(p.due) ? humanDate(resolveDate(p.due)!.date) : p.due}</span>
          )}
          {p.start && <span className="chip chip-date">start {p.start}</span>}
          {p.people.map((h) => (
            <span key={h} className="chip chip-person">
              @{h}
            </span>
          ))}
          {p.labels.map((l) => (
            <span key={l} className="chip chip-label">
              #{l}
            </span>
          ))}
          {p.status && <span className="chip">status: {p.status}</span>}
          {p.bucket && <span className="chip chip-bucket">{p.bucket.replace(/-/g, " ")}</span>}
          {p.projects.map((x) => (
            <span key={x} className="chip chip-project" title="Project tokens are ignored in quick-add">
              +{x}
            </span>
          ))}
          {result?.warnings.map((w) => (
            <span key={w} className="qa-warn">
              {w}
            </span>
          ))}
        </div>
      )}
    </form>
  );
}
